/**
 * useTrackPageAccess — fires one MODULE_ACCESS audit per (module, path) pair.
 *
 * Wired from ProtectedRoute on every route. When `requiredApp` is unset the
 * hook infers a sensible module label from the path so dashboard/settings
 * navigation still shows up in the activity log. Deduped per tab session.
 */
import { useEffect, useRef } from 'react';
import api from '../api/api';

// Path prefix → friendly module key. Order matters: most specific first.
const PATH_MODULE_MAP = [
  ['/dashboard', 'dashboard'],
  ['/profile', 'users'],
  ['/users', 'users'],
  ['/settings', 'settings'],
  ['/company', 'company_mgmt'],
  ['/apps', 'admin_monitoring'],
  ['/admin/monitoring', 'admin_monitoring'],
  ['/tickets', 'helpdesk'],
  ['/sla', 'helpdesk'],
  ['/category', 'helpdesk'],
  ['/team', 'helpdesk'],
  ['/contacts', 'partner'],
  ['/crm', 'crm'],
  ['/marketing', 'marketing'],
  ['/inbox', 'inbox'],
  ['/knowledge-system', 'knowledge_system'],
  ['/csat', 'customer_satisfaction'],
  ['/nexus', 'nexus'],
  ['/projects', 'projects'],
  ['/huddle', 'huddle'],
  ['/email', 'email'],
  ['/integrations', 'admin_monitoring'],
  ['/search', 'dashboard'],
];

const inferModule = (pathname) => {
  if (!pathname) return '';
  for (const [prefix, mod] of PATH_MODULE_MAP) {
    if (pathname.startsWith(prefix)) return mod;
  }
  return '';
};

export const useTrackPageAccess = (moduleTechnicalName, pathname) => {
  const seenRef = useRef(new Set());

  useEffect(() => {
    const mod = moduleTechnicalName || inferModule(pathname);
    if (!mod) return;
    // Dedupe per module (not per path): deep navigation inside one module fires
    // once per tab instead of once per sub-path. Server applies the real bound.
    const key = mod;
    if (seenRef.current.has(key)) return;
    seenRef.current.add(key);

    // Fire-and-forget; failures are silent (heartbeat endpoint bypasses
    // module-install checks, so this lands even when the tenant hasn't
    // installed the admin_monitoring app).
    api.post('admin-monitoring/activity/track-access/', {
      module: mod,
      path: pathname || '',
    }).catch(() => {});
  }, [moduleTechnicalName, pathname]);
};

export default useTrackPageAccess;
